// services/guestReviewApi.js
//
// The ops Reviews page API (tasks/post-checkout-review-and-replies.md Phases 2–3) on the shared secureApi
// instance — the auth header is attached automatically. Each call returns the parsed response body.
//
// Actions pass `preserveBackendError` so the page can read the backend's stable `code`
// (GUEST_REVIEW_xxx) and translate it, instead of secureApi's generic text.

import { secureApi } from './secureApi';

const BASE = '/api/v1/guest-reviews';
const KEEP_CODE = { preserveBackendError: true };

const guestReviewApi = {
  /** Both lists — open items, clean-still-open rows, replies — and their counts, all decided by the server. */
  getQueue: async () => (await secureApi.get(`${BASE}/queue`)).data,

  /** Just the counts — the menu counter's poll. */
  getCounts: async () => (await secureApi.get(`${BASE}/counts`)).data,
  // «صوت الضيف» — per-unit problems and praise (tasks/guest-voice-tab.md step 5).
  getVoice: async (unit) => (await secureApi.get(`${BASE}/voice`, { params: unit ? { unit } : {} })).data,

  /** Edit the prepared review / change the cleaner's call — `{ scores, recommended, publicText, overrideReason }`. */
  editReview: async ({ id, changes }) => (await secureApi.patch(`${BASE}/${id}/review`, changes, KEEP_CODE)).data,

  /** Approve and send our review of the guest. `confirmUnknown`: a person checked Airbnb first. */
  sendReview: async ({ id, confirmUnknown = false }) =>
    (await secureApi.post(`${BASE}/${id}/review/send`, { confirmUnknown }, KEEP_CODE)).data,

  /** "It was reviewed in the Airbnb app" — final. */
  markAlreadyReviewed: async ({ id }) =>
    (await secureApi.post(`${BASE}/${id}/review/already-reviewed`, {}, KEEP_CODE)).data,

  /** Edit the thank-you text. */
  editThankYou: async ({ id, text }) => (await secureApi.patch(`${BASE}/${id}/thank-you`, { text }, KEEP_CODE)).data,

  /** Approve and send the thank-you. */
  sendThankYou: async ({ id, confirmUnknown = false }) =>
    (await secureApi.post(`${BASE}/${id}/thank-you/send`, { confirmUnknown }, KEEP_CODE)).data,

  /** An unclear thank-you a person found in the guest's thread — closes it as sent. */
  confirmThankYouSent: async ({ id }) =>
    (await secureApi.post(`${BASE}/${id}/thank-you/confirm-sent`, {}, KEEP_CODE)).data,

  /** Edit a reply to a guest review — or write it by hand while its draft is still coming. */
  editReply: async ({ id, text }) => (await secureApi.patch(`${BASE}/replies/${id}`, { text }, KEEP_CODE)).data,

  /**
   * Approve and send a reply — with the text exactly as the person saw it; the server refuses if it has
   * changed since (L31). `confirmUnknown`: a person checked the platform first.
   */
  sendReply: async ({ id, text, confirmUnknown = false }) =>
    (await secureApi.post(`${BASE}/replies/${id}/send`, { text, confirmUnknown }, KEEP_CODE)).data,

  /** A person checked the platform and our reply is there — closes it as published. */
  confirmReplyPublished: async ({ id }) =>
    (await secureApi.post(`${BASE}/replies/${id}/confirm-published`, {}, KEEP_CODE)).data,

  /** Edit the private message — ops adds their own words (review-complaint-triage T10). */
  editFollowUp: async ({ id, text }) =>
    (await secureApi.patch(`${BASE}/replies/${id}/follow-up`, { text }, KEEP_CODE)).data,

  /** Approve and send the private message — exactly the text seen; refused if it changed since. */
  sendFollowUp: async ({ id, text, confirmUnknown = false }) =>
    (await secureApi.post(`${BASE}/replies/${id}/follow-up/send`, { text, confirmUnknown }, KEEP_CODE)).data,

  /** No private message needed — final. */
  skipFollowUp: async ({ id, note }) =>
    (await secureApi.post(`${BASE}/replies/${id}/follow-up/skip`, { note }, KEEP_CODE)).data,

  /** An unclear private message a person found in the guest's thread — closes it as sent. */
  confirmFollowUpSent: async ({ id }) =>
    (await secureApi.post(`${BASE}/replies/${id}/follow-up/confirm-sent`, {}, KEEP_CODE)).data,

  /** Skip a review — one reason, an optional note. Final. */
  skipReply: async ({ id, reason, note }) =>
    (await secureApi.post(`${BASE}/replies/${id}/skip`, { reason, note }, KEEP_CODE)).data
};

export default guestReviewApi;
